/**
 * A live's page: the same address a registrant was sent to, turning with the clock and the room, drawn to the
 * confirmed prototype (g-front). Before the room opens it is "You're in" with the countdown and the host's chat;
 * from ten minutes before, the door with the camera check, then the minutes before in the room as it fills; then the
 * live room, where everyone is on camera; then after the live, with the replay and the next session.
 *
 * What counts is today's: the ticket and the room's socket (only once past the door, so somebody still there is not
 * counted as having come), the meeting on its own token, the measurement's joined and stayed, a series moving on to
 * its next session, and the replay asked about until it plays.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { guestLook } from "../../../shared/guest-look";
import { isOver, type MomentRun, upNow } from "../../../shared/live-moments";
import type { ChatToRoom, LiveState } from "../../../shared/live-protocol";
import { offerArrived, offerClosed } from "../../../shared/offer";
import type { LiveTicketResponse, WatchLive, WatchPageResponse, WatchSession } from "../../../shared/types";
import { useMeeting } from "../../live/meeting/useMeeting";
import { chatReducer, EMPTY_CHAT, openRoomChat, type RoomChat, type RoomChatSocket } from "../../live/room/chat";
import { checkClock } from "../../live/room/clock";
import { roomHereUrl, roomSocketUrl } from "../../live/room/url";
import { ApiError, api } from "../../lib/api";
import { applyBrandIcon } from "../brandIcon";
import { holdTicket } from "../liveTicket";
import { laterState, lobbyClock, liveView } from "../liveView";
import { useWatchMeasure } from "../measureWatch";
import { firstName, properName } from "../TalkPlayer";
import { useLiveReactions } from "../Reactions";
import { opensAtMs, rhythmWords, sessionAfter } from "../series";
import { useArrivals } from "../useArrivals";
import { useNow } from "../useNow";
import { useWatchThread } from "../WatchPageView";
import { useGuestGround } from "../room/chrome";
import { usePickMenus } from "../room/pickMenu";
import { LiveAfter } from "./LiveAfter";
import { LiveDoor } from "./LiveDoor";
import { earlyClock, LiveEarly } from "./LiveEarly";
import { LiveRoom } from "./LiveRoom";
import {
  doorWhen,
  earlyDay,
  earlyNote,
  goesLiveWords,
  nextCard,
  sessionDay,
  startsInWords,
  yourClock,
} from "./liveWords";
import "../../styles/guest.css";

/** Leaving the table this close to the end still counts as staying to the end. */
const STAYED_GRACE_MS = 15_000;
/** How long the room says thank you before the page turns to after the live. */
export const THANKS_MS = 6000;
/** How often the door asks who is here. */
const DOOR_HERE_MS = 5000;
const EMPTY_RUNS: MomentRun[] = [];

export function LivePage({
  token,
  data,
  session,
  live,
  onSession,
}: {
  token: string;
  data: WatchPageResponse;
  session: WatchSession;
  live: WatchLive;
  /** A series moved on to another session: the page starts over on it. */
  onSession?: (fresh: WatchSession) => void;
}): ReactNode {
  const navigate = useNavigate();
  const { brand } = data;
  const webinarId = session.webinar_id;
  const hostFirst = firstName(session.host_name);
  const viewer = useMemo(() => ({ first: properName(data.registrant_name || "there"), avatar: data.registrant_avatar_url ?? null }), [data]);
  const now = useNow();
  const look = useMemo(() => guestLook(brand), [brand]);
  useGuestGround(look);
  usePickMenus();
  const yourPage = `/watch/${encodeURIComponent(token)}`;

  // What the API said, renewed after the live while the replay is on its way.
  const [payload, setPayload] = useState(live);
  const [talk, setTalk] = useState(session);
  const [chat, hear] = useReducer(chatReducer, live.chat, (lines): RoomChat => ({ ...EMPTY_CHAT, lines: lines ?? [] }));
  // The page saw the end itself: a socket closed for it, or the API refused a ticket.
  const [sawEnd, setSawEnd] = useState(false);
  // Why remembered: right after the end the kit is still uploading and our replay state is "none", but a room
  // that was recorded while this person sat in it will have a replay.
  const [recorded, setRecorded] = useState(false);
  const [sawLiveAt, setSawLiveAt] = useState<number | null>(null);
  // What the room told a door that has no socket: the live's state comes with who is here.
  const [doorSaw, setDoorSaw] = useState<LiveState | null>(null);
  const state = laterState(laterState(laterState(payload.state, chat.state), doorSaw), sawEnd ? "ended" : null);
  const view = liveView(state, Date.parse(payload.opensAt), now);
  // Why read here: the measurement takes the form's mark off the address once the page has drawn.
  const [landed] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");
  const measure = useWatchMeasure(data.measurement, talk, data.meta_sent_earlier);
  const series = payload.series;

  useEffect(() => {
    document.title = session.title;
  }, [session.title]);
  useEffect(() => {
    applyBrandIcon(brand);
  }, [brand]);

  // ── the ticket: asked for once, and again when it is close to lapsing ──
  const [holder] = useState(() =>
    holdTicket(() => api.watch.liveTicket(token, { webinar_id: webinarId, session_id: live.sessionId })),
  );
  const ticket = useCallback(async (): Promise<LiveTicketResponse> => {
    try {
      return await holder.get();
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) setSawEnd(true);
      throw err;
    }
  }, [holder]);
  const ended = useCallback(() => setSawEnd(true), []);

  // The meeting: everyone on camera, on a token our own door mints. Its camera is the door's.
  const { meeting, state: meetingState } = useMeeting(false, () =>
    api.watch.liveJoin(token, { webinar_id: webinarId, session_id: live.sessionId }),
  );

  // ── the door: passed once, and back to it after leaving or a dropped connection ──
  const [passed, setPassed] = useState(false);
  const [back, setBack] = useState<"left" | "dropped" | null>(null);
  // Why enter at once: after a dropped connection the meeting sits at "failed", and only a press asks it in again
  // (specs/formal/guest-reconnect, JoinAgainWorks).
  const join = useCallback(() => {
    setPassed(true);
    setBack(null);
    meeting.wake();
    if (view === "live") void meeting.enter();
  }, [meeting, view]);
  // By itself when the minutes before run out and the host goes live.
  useEffect(() => {
    if (passed && view === "live" && meetingState.phase === "door") void meeting.enter();
  }, [passed, view, meetingState.phase, meeting]);
  // A meeting that fails puts them back at the door: a drop only after the table, else the meeting's own words.
  useEffect(() => {
    if (meetingState.phase === "in" && meetingState.recording !== "off") setRecorded(true);
  }, [meetingState.phase, meetingState.recording]);
  const lastPhase = useRef(meetingState.phase);
  useEffect(() => {
    const was = lastPhase.current;
    lastPhase.current = meetingState.phase;
    if (meetingState.phase !== "failed" || !passed) return;
    setPassed(false);
    setBack(was === "in" ? "dropped" : null);
  }, [meetingState.phase, passed]);
  const leave = useCallback(() => {
    void meeting.leave();
    setPassed(false);
    setBack("left");
  }, [meeting]);

  // ── the room's socket: in the minutes before once past the door, and at the table once the live is on ──
  // Why not at the door: the room counts a socket as someone being here, so a person still at the door must not
  // have one, or they are marked as having come without ever joining.
  const chatOn = passed && (view === "lobby" || (view === "live" && meetingState.phase === "in"));

  // The live's moments for the workspace's pixel, as the room counts them: at the table once it is on is the talk
  // starting; still there when the host ends it is finishing.
  const seated = view === "live" && meetingState.phase === "in";
  const leftTableAt = useRef<number | null>(null);
  useEffect(() => {
    if (seated) {
      leftTableAt.current = Number.POSITIVE_INFINITY;
      measure.joinedLive(live.sessionId);
    } else if (leftTableAt.current === Number.POSITIVE_INFINITY) {
      leftTableAt.current = Date.now();
    }
  }, [seated, measure, live.sessionId]);
  useEffect(() => {
    const left = leftTableAt.current;
    if (state === "ended" && left !== null && Date.now() - left < STAYED_GRACE_MS) measure.stayedLive(live.sessionId);
  }, [state, measure, live.sessionId]);

  const ticketNow = useRef(ticket);
  ticketNow.current = ticket;
  const [socket, setSocket] = useState<RoomChatSocket | null>(null);
  const reacts = useLiveReactions(socket, 5);
  const reacted = useRef(reacts.heard);
  reacted.current = reacts.heard;
  useEffect(() => {
    if (!chatOn) return;
    const opened = openRoomChat(
      async () => {
        const got = await ticketNow.current();
        return roomSocketUrl(live.liveOrigin, got.room, "chat", got.ticket);
      },
      (event) => (event.type === "reacted" ? reacted.current(event.counts) : hear(event)),
      ended,
    );
    setSocket(opened);
    return () => {
      opened.close();
      setSocket(null);
    };
  }, [chatOn, live.liveOrigin, ended]);
  const send = useCallback((msg: ChatToRoom) => socket?.send(msg) ?? false, [socket]);
  useEffect(() => {
    if (chat.state === "live") setSawLiveAt((at) => at ?? Math.floor(Date.now() / 1000));
  }, [chat.state]);
  const wentLiveAt = payload.wentLiveAt ?? sawLiveAt;

  // ── the door asks who is here without a socket of its own ──
  const atDoor = !passed && (view === "lobby" || view === "live");
  const [doorHere, setDoorHere] = useState<{ here: number; faces: RoomChat["faces"]; hostAway: boolean }>({ here: 0, faces: [], hostAway: false });
  useEffect(() => {
    if (!atDoor) return;
    let alive = true;
    const ask = () => {
      ticketNow
        .current()
        .then((got) => fetch(roomHereUrl(live.liveOrigin, got.room, got.ticket)))
        .then((res) => (res.ok ? (res.json() as Promise<{ here: number; faces?: RoomChat["faces"]; state?: LiveState; hostAway?: boolean }>) : null))
        .then((got) => {
          if (!alive || !got) return;
          setDoorHere({ here: got.here, faces: got.faces ?? [], hostAway: got.hostAway === true });
          if (got.state) setDoorSaw(got.state);
        })
        .catch(() => {});
    };
    ask();
    const every = window.setInterval(ask, DOOR_HERE_MS);
    // Why: a hidden tab's timers run slowly, so it asks again the moment it is shown.
    const shownAgain = () => {
      if (document.visibilityState === "visible") ask();
    };
    document.addEventListener("visibilitychange", shownAgain);
    return () => {
      alive = false;
      window.clearInterval(every);
      document.removeEventListener("visibilitychange", shownAgain);
    };
  }, [atDoor, live.liveOrigin]);

  // A fresh read of the page: this session moved on, or, for a series, another session is theirs now.
  const adopt = useCallback(
    (res: WatchPageResponse) => {
      const fresh = res.sessions.find((s) => s.webinar_id === webinarId);
      if (!fresh?.live) return;
      if (fresh.live.sessionId !== live.sessionId) {
        onSession?.(fresh);
        return;
      }
      setPayload(fresh.live);
      if (fresh.live.replay === "ready") setTalk(fresh);
    },
    [webinarId, live.sessionId, onSession],
  );

  // ── with no socket of its own, the page asks rather than waits ──
  const pushed = socket !== null;
  useEffect(() => {
    if (view === "after" || pushed) return;
    let alive = true;
    const look = () => {
      if (document.visibilityState !== "visible") return;
      api.watch
        .page(token)
        .then((res) => {
          if (alive) adopt(res);
        })
        .catch(() => {});
    };
    const every = window.setInterval(look, 60_000);
    // Why: a tab that was hidden skipped its looks, so it catches up when shown instead of at the next minute.
    document.addEventListener("visibilitychange", look);
    return () => {
      alive = false;
      window.clearInterval(every);
      document.removeEventListener("visibilitychange", look);
    };
  }, [view, pushed, token, adopt]);

  // ── after: the replay, asked about until it plays ──
  const settled = payload.state === "ended";
  const replay = settled ? payload.replay : "preparing";
  useEffect(() => {
    if (view !== "after" || replay === "ready") return;
    // A live that ended with nothing recorded, read on a later visit: nothing is coming.
    if (replay === "none" && !sawEnd) return;
    let alive = true;
    const look = () => {
      api.watch
        .page(token)
        .then((res) => {
          if (alive) adopt(res);
        })
        .catch(() => {});
    };
    // Until the API has caught up with the room's end, ask again soon; then once a minute.
    if (!settled) look();
    const every = window.setInterval(
      () => {
        if (!settled || document.visibilityState === "visible") look();
      },
      settled ? 60_000 : 5_000,
    );
    return () => {
      alive = false;
      window.clearInterval(every);
    };
  }, [view, replay, sawEnd, settled, token, adopt]);

  // ── a series after a session: the next one, and its room opening turns the page to it ──
  const currentSeq = series?.sessions.find((s) => s.id === live.sessionId)?.seq ?? 0;
  const next = series && view === "after" ? sessionAfter(series, currentSeq) : null;
  const nextOpen = next !== null && now >= opensAtMs(next);
  useEffect(() => {
    if (!nextOpen) return;
    let alive = true;
    const look = () => {
      api.watch
        .page(token)
        .then((res) => {
          if (alive) adopt(res);
        })
        .catch(() => {});
    };
    look();
    const every = window.setInterval(() => {
      if (document.visibilityState === "visible") look();
    }, 60_000);
    return () => {
      alive = false;
      window.clearInterval(every);
    };
  }, [nextOpen, token, adopt]);

  // ── the private thread with the host, and its dot ──
  // Why a ref: a word written in the room says the live it was asked in, read at the moment it goes.
  const inRoom = useRef(false);
  inRoom.current = passed && (view === "lobby" || view === "live");
  const stamp = useCallback(() => (inRoom.current ? { live_session_id: live.sessionId } : {}), [live.sessionId]);
  const thread = useWatchThread(token, session, undefined, stamp);
  const fromHost = thread.messages.filter((m) => m.sender === "host").length;
  const [readHost, setReadHost] = useState(() => thread.messages.filter((m) => m.sender === "host" && m.created_at <= session.last_visit_at).length);
  const unreadHost = fromHost > readHost;
  const onReadHost = useCallback(() => setReadHost(fromHost), [fromHost]);

  // ── the live's moments and the offer, at their minute into the live ──
  const [mine, setMine] = useState(session.interactions);
  const momentTalk = useMemo(() => ({ ...talk, interactions: mine }), [talk, mine]);
  const setMineAnswer = useCallback(
    (id: string, value: WatchSession["interactions"][number]["my_response"]) =>
      setMine((all) => all.map((i) => (i.id === id ? { ...i, my_response: value } : i))),
    [],
  );
  const { arrivals, answer, due, close } = useArrivals({ token, session: momentTalk, active: true, setMine: setMineAnswer });
  // Why the room's clock: the server judges an answer against its own minute, so the page reads the minute the same way.
  const [clockOff, setClockOff] = useState(0);
  useEffect(() => {
    if (!seated) return;
    let alive = true;
    void checkClock(live.liveOrigin).then((check) => {
      if (alive && check) setClockOff(check.off);
    });
    return () => {
      alive = false;
    };
  }, [seated, live.liveOrigin]);
  const into = wentLiveAt ? Math.max(0, (now + clockOff) / 1000 - wentLiveAt) : 0;
  // The room's word on what the host has done with the moments, else what the page was read with.
  const runs = chat.runs ?? payload.runs ?? EMPTY_RUNS;
  // Why one rule for everyone: the studio reads what is up the same way, so the host and the room agree.
  const upId = seated && wentLiveAt ? upNow(mine, runs, into, wentLiveAt) : null;
  useEffect(() => {
    if (upId) due(upId);
  }, [upId, due]);
  useEffect(() => {
    for (const run of runs) if (isOver(run)) close(run.interactionId);
  }, [runs, close]);
  const offer = talk.offer;
  // An offer set to show for a while closes once that while has passed in the live.
  const offerDue = !!offer && !!wentLiveAt && offerArrived(offer, into) && !offerClosed(offer, into);

  // ── the end, seen from the table: a thank-you over the room, then after the live ──
  const [thanks, setThanks] = useState(false);
  const lastView = useRef(view);
  useEffect(() => {
    if (lastView.current === "live" && view === "after" && passed) {
      setThanks(true);
      const t = window.setTimeout(() => setThanks(false), THANKS_MS);
      lastView.current = view;
      return () => window.clearTimeout(t);
    }
    lastView.current = view;
  }, [view, passed]);

  const sub = series && currentSeq ? `Session ${currentSeq} · with ${session.host_name}` : `with ${session.host_name}`;
  const agenda = session.chapters.length ? session.chapters.map((c) => c.title) : payload.topic ? [payload.topic] : [];
  const opensAt = yourClock(payload.opensAt);
  const startsMs = Date.parse(payload.startsAt);

  let screen: ReactNode;
  if (view === "before") {
    screen = (
      <LiveEarly
        brand={brand}
        session={session}
        title={session.title}
        sub={sub}
        hostFirst={hostFirst}
        viewer={viewer}
        welcome={[landed ? `You’re in, ${viewer.first}.` : "", series ? series.chip : landed ? "" : `Live with ${hostFirst}`].filter(Boolean).join(" ")}
        day={earlyDay(payload.startsAt, now)}
        clock={earlyClock(startsMs - now)}
        note={earlyNote(payload.startsAt, hostFirst)}
        calendar={session.calendar}
        subscribeWords={series ? `${rhythmWords(series.chip)}, in your calendar` : null}
        agenda={agenda}
        opensAt={opensAt}
        thread={thread}
        unreadHost={unreadHost}
        onReadHost={onReadHost}
        yourPage={yourPage}
      />
    );
  } else if (view === "after" && !thanks) {
    const nx = next && series ? nextCard(next, series.rule.timezone, now) : null;
    screen = (
      <LiveAfter
        brand={brand}
        token={token}
        talk={talk}
        replay={replay}
        reactions={payload.reactions}
        sessionId={series ? live.sessionId : undefined}
        eyebrow={[session.title, currentSeq ? `Session ${currentSeq}` : "", sessionDay(payload.startsAt, payload.timezone)].filter(Boolean).join(" · ")}
        recorded={recorded}
        viewerFirst={viewer.first}
        viewerEmail={data.registrant_email}
        hostFirst={hostFirst}
        offer={offer}
        onConvert={() => offer && measure.press(offer.kind)}
        next={nx}
        calendar={session.calendar}
        thread={thread}
        circleId={session.circle_id}
        yourPage={yourPage}
        tick={measure.tick}
      />
    );
  } else if (!passed || (view === "live" && meetingState.phase !== "in" && !thanks)) {
    screen = (
      <LiveDoor
        brand={brand}
        title={session.title}
        when={doorWhen(payload.startsAt, payload.timezone, now)}
        withWords={series && currentSeq ? `Session ${currentSeq} with` : "with"}
        hostName={session.host_name}
        hostAvatar={session.host_avatar_url}
        hostFirst={hostFirst}
        live={view === "live"}
        startsIn={view === "live" ? null : startsInWords(startsMs - now)}
        here={doorHere.here}
        hostAway={doorHere.hostAway}
        faces={doorHere.faces}
        viewer={viewer}
        back={back}
        joining={passed && (meetingState.phase === "joining" || meetingState.phase === "door")}
        meeting={meeting}
        state={meetingState}
        onJoin={join}
        slug={session.slug}
        siteKey={data.turnstile_site_key ?? null}
        onSomeoneElse={(res) => navigate(res.redirect, { replace: true })}
        yourPage={yourPage}
        poster={session.poster_url}
      />
    );
  } else {
    const wait = view === "lobby";
    screen = (
      <LiveRoom
        wait={wait}
        brand={brand}
        session={session}
        title={session.title}
        sub={wait ? sub : payload.topic ? `with ${hostFirst} · ${payload.topic}` : `with ${hostFirst}`}
        hostFirst={hostFirst}
        viewer={viewer}
        goesLiveAt={goesLiveWords(hostFirst, payload.startsAt)}
        clock={lobbyClock(startsMs, now)}
        agenda={agenda}
        meeting={meeting}
        state={meetingState}
        chat={chat}
        send={send}
        reacts={reacts}
        thread={thread}
        unreadHost={unreadHost}
        onReadHost={onReadHost}
        opensAt={opensAt}
        liveAt={wentLiveAt}
        ended={thanks}
        onAfter={() => setThanks(false)}
        onLeave={leave}
        moment={{ up: arrivals.up, answer, dismiss: arrivals.dismiss }}
        offer={offer ? { offer, due: offerDue, email: data.registrant_email, onConvert: () => measure.press(offer.kind) } : null}
        yourPage={yourPage}
      />
    );
  }
  return (
    <div className="gst" style={look} data-view={view}>
      {screen}
    </div>
  );
}
